import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C } from "../theme/colors";
import { useGSAP } from "../utils/gsapLoader";
import Divider from "../components/Divider";
import Section from "../components/Section";
import AnimatedStat from "../components/AnimatedStat";
import StakeholderLogosSection from "../components/StakeholderLogosSection";
import OrgStructureSection from "../components/OrgStructureSection";
import NeedForRDSection from "../components/NeedForRDSection";
import NewsSection from "../components/NewsSection";

const MEDIA_API_URL = "https://ccapi.rapifuzz.in:8443/api/media-list/";

export default function HomePage() {
  const navigate = useNavigate();
  const setPage = (label) => {
    const routes = { "Research":"/research", "About":"/about", "Contact Us":"/contact", "Media Coverage":"/Media", "stakeholders":"/stakeholders" };
    navigate(routes[label] || "/");
  };
  const gsap = useGSAP();
  const projects = [
    {img:"/uploads/Wind-Tunnel-Facility-3.svg",cat:"Aerospace",title:"Wind Tunnel Facility",desc:"Advanced aerospace research infrastructure for aerodynamic testing and national defence applications."},
    {num:"02",img:"/uploads/Microfluidic-System-2.svg",cat:"Sensors",title:"Electronic Nasal Filter",desc:"Next-generation chemical threat detection through bio-inspired sensory technology."},
    {num:"03",img:"/uploads/Microfluidic-System-1.svg",cat:"Optics",title:"PoST Optical System",desc:"Portable multimode optical detection system for field security operations."},
    {num:"04",img:"/uploads/Post-Optical-System-2.svg",cat:"Materials",title:"Au-LIG Nanocomposite",desc:"In-Situ Metal & Laser Induced Graphene nanocomposite materials research."},
    {num:"05",img:"/uploads/Au-LIG-Nanocomposite-1.svg",cat:"Diagnostics",title:"Microfluidic System",desc:"Chemiluminescence-based rapid chemical analysis for on-site threat identification."},
  ];
  const staticMedia = [
    {name:"Economic Times",url:"https://economictimes.indiatimes.com"},
    {name:"Hindustan Times",url:"https://hindustantimes.com"},
    {name:"Indian Express",url:"https://indianexpress.com"},
    {name:"India Today",url:"https://indiatoday.in"},
    {name:"Telangana Today",url:"https://telanganatoday.com"},
    {name:"Career360",url:"https://careers360.com"},
    // {name:"The Hindu",url:"https://thehindu.com"},
    // add more items here anytime — homepage will auto-show a "View All" button once there are more than 6
  ];

  const [apiMedia, setApiMedia] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetch(MEDIA_API_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then(json => {
        if (cancelled) return;
        const list = Array.isArray(json?.data) ? json.data : [];
        if (list.length) {
          setApiMedia(list.map(m => ({ name: m.title, url: m.link })));
        }
      })
      .catch(() => {
        // Silently keep the static fallback list on any failure
      });

    return () => { cancelled = true; };
  }, []);

  const media = apiMedia && apiMedia.length ? apiMedia : staticMedia;

  // ⬇️ DEMO PLACEHOLDER — replace with your real background file
  const BACKGROUND_IMAGE_URL = "uploads/DSC_1654.JPG";

  return (
    <div className="page-fade">
      <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:"90px 40px 60px"}}>
        {/* BACKGROUND IMAGE — swap BACKGROUND_IMAGE_URL above with your real photo */}
        <img src={BACKGROUND_IMAGE_URL} alt="" aria-hidden="true" onError={e => { e.target.style.display="none"; }} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center 35%",zIndex:0}}/>
        {/* Overlay lightened (was 0.72/0.68/0.85) so the background photo is actually visible */}
        <div style={{position:"absolute",inset:0,background:`linear-gradient(180deg,rgba(8,21,64,0.45) 0%,rgba(8,21,64,0.4) 50%,rgba(8,21,64,0.6) 100%)`,zIndex:1}}/>
        <div style={{position:"absolute",inset:0,background:`linear-gradient(140deg, ${C.navyDark} 0%, ${C.navy} 55%, ${C.blue} 100%)`,zIndex:0,opacity:0.15}}/>
        <div style={{position:"relative",zIndex:2,display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",maxWidth:860,width:"100%"}}>
          <div>
            <img
              src="/uploads/crens_logo_final 1.png"
              alt="CRENS Logo"
              style={{
                width: 90,
                height: 90,
                objectFit: "contain",
              }}
            />
          </div>
          <h1 style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:"clamp(1.9rem,4.2vw,3.2rem)",color:C.white,lineHeight:1.18,letterSpacing:"-0.3px",marginBottom:20,textShadow:"0 2px 20px rgba(0,0,0,0.4)"}}>
            Centre for Research Excellence in<br/>National Security (CRENS)
          </h1>
          <div style={{width:56,height:3,background:C.goldLight,borderRadius:2,marginBottom:20}}/>
          <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:"clamp(13px,1.5vw,16px)",color:"rgba(255,255,255,0.7)",lineHeight:1.75,marginBottom:38,maxWidth:560}}>
            A Centre of Excellence of BITS Pilani &nbsp;·&nbsp; Established 6th August 2024
          </p>
          <div style={{display:"flex",gap:16,flexWrap:"wrap",justifyContent:"center"}}>
            <button onClick={() => setPage("Research")} style={{background:C.goldLight,color:C.navyDark,border:"none",borderRadius:6,padding:"14px 34px",fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:15,cursor:"pointer",transition:"all .22s",letterSpacing:"0.2px",boxShadow:"0 4px 18px rgba(212,160,23,0.35)"}} onMouseEnter={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={e=>{e.currentTarget.style.background=C.goldLight;e.currentTarget.style.transform="none";}}>
              Explore Research
            </button>
            <button onClick={() => setPage("About")} style={{background:"transparent",color:C.white,border:`2px solid rgba(255,255,255,0.5)`,borderRadius:6,padding:"12px 34px",fontFamily:"Arial,Helvetica,sans-serif",fontWeight:600,fontSize:15,cursor:"pointer",transition:"all .22s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.white;e.currentTarget.style.background="rgba(255,255,255,0.1)";e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.5)";e.currentTarget.style.background="transparent";e.currentTarget.style.transform="none";}}>
              Learn More
            </button>
          </div>
        </div>
      </div>

      <OrgStructureSection gsap={gsap}/>

      <Section alt>
        <div style={{textAlign:"center",marginBottom:52}}>
          <span className="section-tag">Research Portfolio</span>
          <h2 style={{fontFamily:"var(--font-heading)",fontWeight:600,color:C.navyDark,fontSize:"clamp(1.6rem,3vw,2.4rem)",marginTop:10}}>Active Technology Projects</h2>
          <div style={{display:"flex",justifyContent:"center"}}>
            <Divider/>
          </div>
          <p className="body" style={{margin:"-14px auto"}}>Cutting-edge indigenous research projects driving India's national security capabilities forward.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:20}}>
          {projects.map((p,i) => (
            <div key={i} className="card" style={{padding:"28px 24px",borderLeft:`3px solid ${C.accentDeep}`}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              
                <div style={{width:1,height:32,background:C.borderLight}}/>
                <img src={p.img} alt={p.cat} style={{width:24,height:24,objectFit:"contain"}}/>
              </div>
              <span className="tag" style={{marginBottom:10}}>{p.cat}</span>
              <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:16,fontWeight:600,color:C.navyDark,marginTop:10,marginBottom:10,lineHeight:1.3}}>{p.title}</div>
              <p className="body" style={{fontSize:14,lineHeight:1.75,margin:0}}>{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <StakeholderLogosSection gsap={gsap} setPage={setPage} showAll={false}/>
      <NeedForRDSection gsap={gsap}/>

      <Section>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:80,alignItems:"center"}} className="grid-2">
          <div>
            <span className="section-tag">Inauguration</span>
            <h2 style={{fontFamily:"var(--font-heading)",fontWeight:600,color:C.navyDark,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",marginTop:10}}>A Landmark Launch</h2>
            <Divider/>
            <p className="body" style={{marginBottom:20}}>CRENS was inaugurated by Dr G Satheesh Reddy, Former Chairman of DRDO and Scientific Adviser to the Defence Minister, on 6th August 2024.</p>
            <div style={{padding:"22px 26px",background:C.goldBg,borderLeft:`3px solid ${C.gold}`,borderRadius:"0 10px 10px 0"}}>
              <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:15,color:C.text,lineHeight:1.85,fontStyle:"italic",marginBottom:10}}>"CRENS will play a crucial role in making the country self-reliant in the national security scientific and technological domain."</p>
              <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,fontWeight:700,color:C.gold,letterSpacing:0.5}}>— Dr G Satheesh Reddy</span>
            </div>
          </div>
          <div>
            <span className="section-tag">In The Press</span>
            <h2 style={{fontFamily:"var(--font-heading)",fontWeight:600,color:C.navyDark,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",marginTop:10}}>Media Coverage</h2>
            <Divider/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {media.slice(0,6).map((m,i) => (
                <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" style={{textDecoration:"none",padding:"16px 18px",borderRadius:10,border:`1px solid ${C.border}`,background:C.white,display:"flex",alignItems:"center",justifyContent:"space-between",transition:"all .2s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accentDeep;e.currentTarget.style.background=C.accent;e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.white;e.currentTarget.style.transform="none";}}>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:600,color:C.navyDark}}>{m.name}</span>
                  <span style={{color:C.blue,fontSize:13}}>↗</span>
                </a>
              ))}
            </div>
            {media.length > 6 && (
              <button onClick={() => setPage("Media Coverage")} style={{marginTop:16,background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:700,color:C.blue,padding:0}}>
                View All Media Coverage ({media.length}) →
              </button>
            )}
          </div>
        </div>
      </Section>

      <NewsSection/>

      <div style={{background:`linear-gradient(135deg, ${C.navyDark}, ${C.blue})`,padding:"60px 40px",textAlign:"center",position:"relative",overflow:"hidden"}}>
        <div style={{maxWidth:640,margin:"0 auto",position:"relative"}}>
          <h2 style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:"clamp(1.5rem,3vw,2.2rem)",color:C.white,marginBottom:14}}>Collaborate with CRENS</h2>
          <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:15,color:"rgba(255,255,255,0.65)",marginBottom:28}}>Partner with us to advance India's national security through research, innovation, and skill development.</p>
          <button className="btn-primary" onClick={() => setPage("Contact Us")} style={{background:C.white,color:C.navyDark}}>Get in Touch →</button>
        </div>
      </div>
    </div>
  );
}